import React, { useEffect, useMemo, useRef, useState, useId } from "react";
import "./styles.css";

/**
 * SearchableMultiSelect
 *
 * Props:
 * - options: Array<{ value: string|number, label: string, disabled?: boolean }>
 * - value:   Array<string|number> | string | number | null // when single=true you can pass a scalar
 * - onChange(nextValues: Array<string|number> | string | number | null) // when single=true emits a scalar/null
 * - placeholder?: string
 * - disabled?: boolean
 * - renderLabel?: (opt) => ReactNode  // custom label renderer
 * - noResultsText?: string
 * - selectAllLabel?: string
 * - clearLabel?: string
 * - maxMenuHeight?: number (px)
 * - chipLimit?: number   // show "+N more" when exceeded
 * - closeOnSelect?: boolean // close dropdown after selecting an option
 *
 * Notes:
 * - “Select All” applies to the *filtered* list.
 * - Full keyboard control: ArrowUp/Down to move, Enter/Space to toggle, Esc to close.
 */
export default function SearchableMultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = "Select…",
  inputClassNameModifier = "",
  disabled = false,
  renderLabel,
  noResultsText = "No results",
  selectAllLabel = "Select all (filtered)",
  clearLabel = "Clear",
  maxMenuHeight = 240,
  chipLimit = 4,
  closeOnSelect = false,
  single = false, // when true, behave like a single-select dropdown
  orderOptions = "selected-first-alpha", // "selected-first-alpha" | "alpha" | "selected-first" | "none" | ((opts) => opts)
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const radioGroup = useId();

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Selection helpers
  const normalizedValue = useMemo(() => {
    if (single) {
      if (value === null || value === undefined) return [];
      if (Array.isArray(value)) return value.slice(0, 1);
      return [value];
    }
    return Array.isArray(value) ? value : [];
  }, [value, single]);

  // Filter (case-insensitive) by label
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label ?? "").toLowerCase().includes(q));
  }, [options, query]);

  // Order options (default: selected first, then alphabetical)
  const orderedFiltered = useMemo(() => {
    const base = [...filtered];
    if (!orderOptions || orderOptions === "none") return base;
    if (typeof orderOptions === "function") return orderOptions(base);

    const byLabel = (a, b) => (a.label || "").localeCompare(b.label || "");
    const sortAlpha = () => base.sort(byLabel);

    if (orderOptions === "alpha") {
      return sortAlpha();
    }

    if (orderOptions === "selected-first") {
      const selectedValues = new Set(normalizedValue);
      return base
        .sort(byLabel)
        .sort((a, b) => {
          const aSel = selectedValues.has(a.value) ? 0 : 1;
          const bSel = selectedValues.has(b.value) ? 0 : 1;
          return aSel - bSel;
        });
    }

    if (orderOptions === "selected-first-alpha") {
      const selectedValues = new Set(normalizedValue);
      return base
        .sort(byLabel)
        .sort((a, b) => {
          const aSel = selectedValues.has(a.value) ? 0 : 1;
          const bSel = selectedValues.has(b.value) ? 0 : 1;
          if (aSel !== bSel) return aSel - bSel;
          return (a.label || "").localeCompare(b.label || "");
        });
    }

    return base;
  }, [filtered, normalizedValue, orderOptions]);

  const selectedSet = useMemo(() => new Set(normalizedValue), [normalizedValue]);
  const isAllFilteredSelected = useMemo(() => {
    if (orderedFiltered.length === 0) return false;
    return orderedFiltered.every((o) => o.disabled || selectedSet.has(o.value));
  }, [orderedFiltered, selectedSet]);

  const toggle = (opt) => {
    if (opt.disabled) return;
    if (single) {
      const nextValue = selectedSet.has(opt.value) ? null : opt.value;
      onChange?.(nextValue);
      setOpen(false);
      return;
    }
    const next = new Set(selectedSet);
    if (next.has(opt.value)) next.delete(opt.value);
    else next.add(opt.value);
    onChange?.(Array.from(next));
    if (closeOnSelect) setOpen(false);
  };

  const selectAllFiltered = () => {
    const next = new Set(selectedSet);
    orderedFiltered.forEach((o) => {
      if (!o.disabled) next.add(o.value);
    });
    onChange?.(Array.from(next));
  };

  const clearFiltered = () => {
    if (orderedFiltered.length === 0) return;
    const filteredValues = new Set(orderedFiltered.map((o) => o.value));
    const next = value.filter((v) => !filteredValues.has(v));
    onChange?.(next);
  };

  const clearAll = () => {
    onChange?.([]);
  };

  // Outside click to close
  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Focus search when opening
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  // Keyboard navigation
  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    const focusable = orderedFiltered.filter((o) => !o.disabled);
    const clampIndex = (i) =>
      Math.max(0, Math.min(focusable.length - 1, i));

    const isSearchInputActive = inputRef.current && inputRef.current === e.target;
    if (open && isSearchInputActive && (e.key === " " || e.key === "Spacebar")) {
      return; // allow typing spaces in the search box without toggling selections
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => clampIndex(i + 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => clampIndex(i - 1));
        break;
      case "Home":
        e.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        e.preventDefault();
        setActiveIndex(Math.max(0, focusable.length - 1));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (focusable.length > 0) toggle(focusable[activeIndex]);
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
      default:
        break;
    }

    // Keep active item in view
    if (listRef.current) {
      const item = listRef.current.querySelector(
        `[data-index="${activeIndex}"]`
      );
      if (item) {
        const { top, bottom } = item.getBoundingClientRect();
        const parent = listRef.current.getBoundingClientRect();
        if (top < parent.top) item.scrollIntoView({ block: "nearest" });
        if (bottom > parent.bottom) item.scrollIntoView({ block: "nearest" });
      }
    }
  };

  // Chips (selected tags)
  const selectedOptions = useMemo(() => {
    const byVal = new Map(options.map((o) => [o.value, o]));
    return normalizedValue
      .map((v) => byVal.get(v))
      .filter(Boolean)
      .map((o) => ({ value: o.value, label: o.label }));
  }, [options, value]);

  return (
    <div
      ref={rootRef}
      className={`sms${single ? " sms--single" : ""}`}
      onKeyDown={onKeyDown}
      aria-haspopup="listbox"
      aria-expanded={open}
    >
      {/* Control / Input */}
      <button
        type="button"
        className={`sms__control ${inputClassNameModifier}`}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <div className="sms__value">
          {selectedOptions.length === 0 ? (
            <span className="sms__placeholder">{placeholder}</span>
          ) : single ? (
            <span className="sms__single-label">{selectedOptions[0].label}</span>
          ) : (
            <>
              {selectedOptions.slice(0, chipLimit).map((s) => (
                <span className="sms__chip" key={s.value} title={s.label}>
                  {s.label}
                  <span
                    className="sms__chip-x"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle({ value: s.value });
                    }}
                    aria-label={`Remove ${s.label}`}
                  >
                    ×
                  </span>
                </span>
              ))}
              {selectedOptions.length > chipLimit && (
                <span className="sms__chip sms__chip--more">
                  +{selectedOptions.length - chipLimit} more
                </span>
              )}
            </>
          )}
        </div>
        <span className="sms__arrow" aria-hidden>▾</span>
      </button>

      {/* Menu */}
      {open && (
        <div className="sms__menu" role="dialog">
          <div className="sms__search">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="sms__input"
              aria-label="Search options"
            />
          </div>

          <ul
            ref={listRef}
            className="sms__list"
            role="listbox"
            aria-multiselectable={single ? "false" : "true"}
            style={{ maxHeight: maxMenuHeight }}
          >
            {orderedFiltered.length === 0 && (
              <li className="sms__empty" aria-disabled="true">
                {noResultsText}
              </li>
            )}
            {orderedFiltered.map((opt, i) => {
              const isSelected = selectedSet.has(opt.value);
              const isActive = !opt.disabled &&
                orderedFiltered.filter(o => !o.disabled).indexOf(opt) === activeIndex;
              return (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={!!opt.disabled}
                  className={[
                    "sms__option",
                    isSelected && "is-selected",
                    opt.disabled && "is-disabled",
                    isActive && "is-active",
                  ].filter(Boolean).join(" ")}
                  onMouseDown={(e) => e.preventDefault()} // prevent focus loss
                  onClick={() => toggle(opt)}
                  data-index={i}
                >
                  <input
                    type={single ? "radio" : "checkbox"}
                    className="sms__input_box"
                    name={single ? `sms-single-${radioGroup}` : undefined}
                    tabIndex={-1}
                    checked={isSelected}
                    onChange={() => {}}
                    disabled={opt.disabled}
                  />
                  <span className="sms__label">
                    {renderLabel ? renderLabel(opt) : opt.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
